/**
 * "Mi licencia" hooks (Sprint 227 — Stream C, REQ-BIL-018 / ADR-097).
 *
 * `useMyLicense` fetches the read-only license view of the ACTIVE tenant via
 * `GET /api/v1/licenses/me` (session-scoped; no tenant prefix).
 *
 * `useLicenseAccess` + `hasLicenseAccess` drive the sidebar menu gating
 * fail-closed (SEC-003): the menu is shown ONLY when the endpoint returns 200;
 * any error (403 LICENSE_VIEW_DENIED, network, loading) hides it — hiding is
 * presentation only, the API is the authority.
 */

import { useQuery } from '@tanstack/react-query';
import { rawApi } from '@/lib/api-client';
import type { LicenseViewResponse } from '../types';

const LICENSE_ENDPOINT = 'licenses/me';

export function useMyLicense() {
  return useQuery({
    queryKey: ['my-license'],
    queryFn: async () => {
      const response = await rawApi.get<LicenseViewResponse>(LICENSE_ENDPOINT);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    // 403 LICENSE_VIEW_DENIED is expected for members: surface it as a
    // regular error state (the page renders nothing on error).
    retry: false,
  });
}

export function useLicenseAccess() {
  const query = useMyLicense();
  return query;
}

/**
 * Resolve the menu-visibility flag from the hook payload. Loading and any
 * error (403 included) resolve to false — fail-closed (SEC-003).
 */
export function hasLicenseAccess(data: LicenseViewResponse | undefined): boolean {
  return data !== undefined;
}